import type {
    PolicyCategory,
    PolicyConstraint,
    PolicyDefinition,
    PolicyPermission
} from "@/pages/ProgramPolicies/policiesMocks";
import { POLICY_CATALOG } from "@/pages/shared/programMocks";
import type { NextDistribution, Policy, Program, ProgramStatus } from "@/pages/shared/programMocks";
import type { ClientAssignedProgramView, ClientEffectivePolicy, ClientProgram } from "@/types/programCenteredYaas";

const REQUIRED_PROGRAM_CONFIGURATION_RULE_TYPES = [
    "program-duration",
    "rewards-model",
    "settlement-cycle",
    "client-added-funding-sources"
] as const;

const REQUIRED_PROGRAM_CONFIGURATION_RULE_TYPE_SET = new Set<string>(REQUIRED_PROGRAM_CONFIGURATION_RULE_TYPES);

function stringMetadata(metadata: Record<string, unknown> | null, key: string): string | null {
    const value = metadata?.[key];
    return typeof value === "string" && value.trim() ? value : null;
}

function numberMetadata(metadata: Record<string, unknown> | null, key: string): number | null {
    const value = metadata?.[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringArrayMetadata(metadata: Record<string, unknown> | null, key: string): string[] | null {
    const value = metadata?.[key];
    if (!Array.isArray(value)) return null;
    const strings = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return strings.length ? strings : null;
}

function recordMetadata(metadata: Record<string, unknown> | null, key: string): Record<string, unknown> | null {
    const value = metadata?.[key];
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function programStatus(status: ClientProgram["status"]): ProgramStatus {
    if (status === "Active") return "active";
    if (status === "Ended") return "ended";
    return "in_progress";
}

function statusLabel(status: ProgramStatus): string {
    if (status === "active") return "Active";
    if (status === "ended") return "Ended";
    return "Draft";
}

function periodValueForDays(days: number | null): string {
    if (!days) return "3m";
    if (days <= 45) return "1m";
    if (days <= 135) return "3m";
    if (days <= 270) return "6m";
    return "12m";
}

/**
 * The Program view's effective Policy Instances, or an empty list.
 *
 * yaas-api no longer serializes `effectivePolicies` on the client Program
 * view, so every live payload omits it and a direct read throws. This is the
 * single boundary where that absence becomes an empty list; callers below then
 * fall back to the Program's own metadata for the values the policies carried.
 */
function effectivePoliciesOf(view: ClientAssignedProgramView): ClientEffectivePolicy[] {
    return view.effectivePolicies ?? [];
}

function policyByRuleType(
    policies: ClientEffectivePolicy[],
    ruleType: (typeof REQUIRED_PROGRAM_CONFIGURATION_RULE_TYPES)[number]
): ClientEffectivePolicy | null {
    return policies.find((policy) => policy.ruleType === ruleType) ?? null;
}

function policyString(policy: ClientEffectivePolicy | null, key: string): string | null {
    const value = policy?.parameters?.[key];
    return typeof value === "string" && value.trim() ? value : null;
}

function policyNumber(policy: ClientEffectivePolicy | null, key: string): number | null {
    const value = policy?.parameters?.[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function daysBetween(startAt: string | null, endAt: string | null): number | null {
    if (!startAt || !endAt || startAt === "immediate" || endAt === "immediate") return null;
    const start = Date.parse(startAt);
    const end = Date.parse(endAt);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return Math.max(1, Math.round((end - start) / 86_400_000));
}

export function isRequiredProgramConfigurationPolicy(ruleType: string | null | undefined): boolean {
    return typeof ruleType === "string" && REQUIRED_PROGRAM_CONFIGURATION_RULE_TYPE_SET.has(ruleType);
}

/**
 * Sim-only per-program `nextDistribution` overrides, keyed by program uuid.
 * Seeded programs (mockApi `SEED_CLIENT_RUN_PROGRAMS`) carry no distribution
 * cycle in their bare API shape, so `ProgramDetail` — which gates the
 * NextDistributionMeter behind `isActive && program.nextDistribution` —
 * never shows it. This map lets a specific active seeded program surface a
 * realistic accrued/projected meter + live countdown without touching the
 * seeder or every other program (values mirror the local PROGRAMS fixtures).
 */
const SEEDED_NEXT_DISTRIBUTION: Record<string, NextDistribution> = {
    // "USDv Cashback Rewards" (Active) — fully-populated read view for
    // visual assessment. ~2.9 days to the next weekly distribution.
    "client-run-program-seed-1": {
        accrued: 18500,
        projected: 27000,
        cycleDays: 7,
        inSeconds: 248400
    }
};

/** Look up a policy template from the shared catalog by id (the same
 *  templates the program builder / edit-in-place resolve against, so the
 *  seeded read list and the inline editor stay in lockstep). */
function catalogPolicy(id: string): Policy {
    const found = POLICY_CATALOG.find((policy) => policy.id === id);
    if (!found) throw new Error(`Unknown seed policy id: ${id}`);
    return found;
}

interface SeededPolicySet {
    policies: Policy[];
    policyValues: Record<string, number | string>;
}

/**
 * Sim-only per-program attached-policy overrides, keyed by program uuid.
 * The seeded ClientRun programs (mockApi `SEED_CLIENT_RUN_PROGRAMS`) carry
 * `effectivePolicies: []` in their bare view, so `clientProgramViewToProgram`
 * would return an empty `policies` list and the Policies section would render
 * empty. This map lets a specific active seeded program surface a populated,
 * varied `PolicyRow` list (read view + edit-in-place, which share the same
 * `policies` state) without touching the seeder or the other programs (which
 * stay empty). Mirrors `SEEDED_NEXT_DISTRIBUTION`. Policies are pulled from
 * the shared `POLICY_CATALOG` so their ids/kinds match the builder catalog and
 * inline editing resolves the right control.
 */
const SEEDED_POLICIES: Record<string, SeededPolicySet> = {
    // "USDv Cashback Rewards" (Active) — a varied, fully-configured policy
    // set: a scalar minimum-balance threshold, a multi-tranche time-held
    // boost, and a whitelist access-control rule with a few addresses.
    "client-run-program-seed-1": {
        policies: [
            catalogPolicy("minimum-threshold"),
            catalogPolicy("time-held-tranche"),
            catalogPolicy("wallet-list")
        ],
        policyValues: {
            // Require ≥ 500 USDv to qualify.
            "minimum-threshold": 500,
            // Time-held boost tranches — longer holds earn a bigger multiplier.
            "time-held-tranche": JSON.stringify([
                { amount: 0, multiplier: 1 },
                { amount: 30, multiplier: 1.25 },
                { amount: 90, multiplier: 1.5 },
                { amount: 180, multiplier: 2 }
            ]),
            // Whitelist a few wallets (JSON {mode, addresses}).
            "wallet-list": JSON.stringify({
                mode: "whitelist",
                addresses: [
                    "7Np41oeYqPefeNQEHSv1UDhYrehxin3NStELkfyBgPMd",
                    "E9Yffvreri1KbHhE84J7UQoPjphL4KLC1ZwfE8PXWi7x",
                    "GThUX1Mtg2T2VbqDmVr1H8K5w3Mtsg6Cv8mpQ4kXyz9"
                ]
            })
        }
    }
};

export function clientProgramViewToProgram(view: ClientAssignedProgramView): Program {
    const program = view.program;
    const metadata = program.metadata;
    const builderConfig = recordMetadata(metadata, "builderConfig");
    const status = programStatus(program.status);
    const regions = stringArrayMetadata(metadata, "regions");
    const policies = effectivePoliciesOf(view);
    const durationPolicy = policyByRuleType(policies, "program-duration");
    const rewardsPolicy = policyByRuleType(policies, "rewards-model");
    const settlementPolicy = policyByRuleType(policies, "settlement-cycle");
    const metadataPeriodDays = numberMetadata(metadata, "periodDays") ?? numberMetadata(builderConfig, "periodDays");
    const durationMode = policyString(durationPolicy, "mode");
    const periodDays =
        durationMode === "fixed"
            ? (daysBetween(policyString(durationPolicy, "startAt"), policyString(durationPolicy, "endAt")) ??
              metadataPeriodDays)
            : metadataPeriodDays;
    const baseApyBps = policyNumber(rewardsPolicy, "baseApyBps");
    const baseApy =
        baseApyBps === null
            ? (numberMetadata(metadata, "baseApy") ?? numberMetadata(builderConfig, "baseApy"))
            : baseApyBps / 100;
    const rewardsPct = policyNumber(rewardsPolicy, "rewardsPct");
    const budget = numberMetadata(metadata, "budget") ?? numberMetadata(builderConfig, "budget");
    const metadataProgramType = stringMetadata(metadata, "programType") ?? stringMetadata(builderConfig, "programType");
    const budgetMode = stringMetadata(metadata, "budgetMode") ?? stringMetadata(builderConfig, "budgetMode");
    const settlementCycleUnit = policyString(settlementPolicy, "unit");
    const seededPolicies = SEEDED_POLICIES[program.uuid];

    return {
        id: program.uuid,
        name: program.name,
        subtitle:
            program.description ??
            (program.runType === "ClientRun"
                ? "Company Recipient-run incentive program"
                : "Distributor-assigned incentive program"),
        status,
        statusLabel: statusLabel(status),
        region: regions?.[0] ?? stringMetadata(metadata, "region") ?? "global",
        target: program.participationModel === "ClientEndUserEnrollments" ? "holders" : "agents",
        period: periodValueForDays(periodDays),
        periodDays: periodDays ?? undefined,
        programType: durationMode === "continuous" || metadataProgramType === "continuous" ? "continuous" : "fixed",
        budgetMode: budgetMode === "open" ? "open" : "fixed",
        baseApy: baseApy ?? undefined,
        rewardsPct: rewardsPct ?? 100,
        settlementCycleLength: policyNumber(settlementPolicy, "length") ?? 30,
        settlementCycleUnit: settlementCycleUnit === "weeks" ? "weeks" : "days",
        policies: seededPolicies?.policies ?? [],
        budget: budget ?? 100000,
        daysElapsed: status === "active" ? 0 : undefined,
        nextDistribution: status === "active" ? SEEDED_NEXT_DISTRIBUTION[program.uuid] : undefined,
        policyValues: seededPolicies?.policyValues ?? {}
    };
}

export function clientProgramViewsToPrograms(views: ClientAssignedProgramView[]): Program[] {
    return views.map(clientProgramViewToProgram);
}

function isClientRunProgramView(view: ClientAssignedProgramView): boolean {
    return view.program.runType === "ClientRun";
}

export function clientProgramViewsToClientRunPrograms(views: ClientAssignedProgramView[]): Program[] {
    return views.filter(isClientRunProgramView).map(clientProgramViewToProgram);
}

function policyCategory(category: ClientEffectivePolicy["category"]): PolicyCategory {
    switch (category) {
        case "Allocation":
            return "allocation";
        case "Scheduling":
            return "distribution-schedule";
        case "CapsAndLimits":
            return "allocation";
        case "Compliance":
        case "Eligibility":
        default:
            return "eligibility";
    }
}

function policyPermission(permission: ClientEffectivePolicy["permission"]): PolicyPermission {
    if (permission === "Locked") return "locked";
    if (permission === "Delegated") return "delegated";
    return "constrained";
}

function policyConstraint(policy: ClientEffectivePolicy): PolicyConstraint {
    const config = policy.constraintConfig;
    if (config?.kind === "options" && Array.isArray(config.options)) {
        const options = config.options.filter((item): item is string => typeof item === "string");
        return { kind: "options", options };
    }
    const min = typeof config?.min === "number" ? config.min : 0;
    const max = typeof config?.max === "number" ? config.max : Number.POSITIVE_INFINITY;
    const unit = typeof config?.unit === "string" ? config.unit : "";
    return { kind: "range", min, max, unit };
}

export function clientEffectivePolicyToPolicyDefinition(
    policy: ClientEffectivePolicy,
    programUuid: string
): PolicyDefinition {
    return {
        id: policy.uuid,
        typeId: policy.ruleType || policy.uuid,
        type: policy.label || policy.ruleType || "Program Policy",
        category: policyCategory(policy.category),
        value: policy.currentValue || "Configured",
        detail: policy.description || policy.constraintSummary || "Policy configured by the distributor.",
        programIds: policy.programUuid ? [policy.programUuid] : [programUuid],
        constraint: policyConstraint(policy),
        permission: policyPermission(policy.permission),
        wallets: Array.isArray(policy.parameters.wallets)
            ? policy.parameters.wallets.filter((item): item is string => typeof item === "string")
            : undefined
    };
}

export function clientProgramViewsToPolicyDefinitions(views: ClientAssignedProgramView[]): PolicyDefinition[] {
    const byId = new Map<string, PolicyDefinition>();

    for (const view of views.filter(isClientRunProgramView)) {
        for (const policy of effectivePoliciesOf(view)) {
            if (isRequiredProgramConfigurationPolicy(policy.ruleType)) continue;
            const next = clientEffectivePolicyToPolicyDefinition(policy, view.program.uuid);
            const existing = byId.get(next.id);
            if (existing) {
                existing.programIds = Array.from(new Set([...existing.programIds, ...next.programIds]));
            } else {
                byId.set(next.id, next);
            }
        }
    }

    return [...byId.values()];
}
